import { CircularProgress } from '@mui/material';
import UPButton from 'components/button';
import { UPToggleButton } from 'components/button/styles';
import UPButtonGroup from 'components/button/UPGroupButton';
import UPDropdownWithSearch from 'components/dropdown-with-search/UPDropdownWithSearch';
import UPMultipleSelectAutocomplete from 'components/dropdown-with-search/UPMultipleSelectAutocomplete';
import { UPDropdownItem } from 'components/dropdown/types';
import ErrorBar from 'components/error-bar';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { PlanningInThePastState } from 'constants/PlanningInThePastState';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__PLANNING_API_USER_FIELD,
  CONTRACT_COMPLIANCE__PLANNING_API_USER_HINT,
  CONTRACT_COMPLIANCE__PLANNING_APIS_FIELD,
  CONTRACT_COMPLIANCE__PLANNING_APIS_HINT,
  CONTRACT_COMPLIANCE__PLANNING_NO_ELIGIBLE_API_USERS,
  CONTRACT_COMPLIANCE__PLANNING_NO_LINKED_APIS,
  CONTRACT_COMPLIANCE__PLANNING_PANEL_NEW_OVERRIDE,
  CONTRACT_COMPLIANCE__PLANNING_PANEL_ROLE_DEFAULT,
  CONTRACT_COMPLIANCE__PLANNING_PANEL_USER_OVERRIDE,
  CONTRACT_COMPLIANCE__PLANNING_PERMISSION_FIELD,
  CONTRACT_COMPLIANCE__PLANNING_REMOVE_OVERRIDE,
  CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
} from 'translations/constants';
import JustificationFields from '../JustificationFields';
import { HintText, PanelErrorRow, RemoveExceptionLink } from '../styles';
import { ROLE_LABEL_KEYS, STATE_LABEL_KEYS, STATE_SLUGS, STATES, TEST_ID, WORKER_CLASSES } from './constants';
import { UsePlanningInThePastPanelResult } from './hooks/usePlanningInThePastPanel';

interface PlanningInThePastPanelProps {
  panel: UsePlanningInThePastPanelResult;
}

const PANEL_TEST_ID = `${TEST_ID}-panel`;

/** One panel for a Role default and for a per-user override: every worker class with its three-state control. */
const PlanningInThePastPanel = ({ panel }: PlanningInThePastPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const { mode, role, status, entry, states, selectedApiUserId, selectedScopes, availableScopes, isSaving } = panel;
  const isBusy = isSaving || panel.justification.isUploading;
  const isOverride = mode !== 'role';
  const userItems: UPDropdownItem[] = useMemo(
    () => panel.eligibleUsers.map(user => ({ label: user.name, value: String(user.id) })),
    [panel.eligibleUsers],
  );
  const scopeItems: UPDropdownItem[] = useMemo(
    () => availableScopes.map(scope => ({ label: scope, value: scope })),
    [availableScopes],
  );
  const selectedScopeItems = useMemo(
    () => scopeItems.filter(item => selectedScopes.includes(String(item.value))),
    [scopeItems, selectedScopes],
  );

  const getTitle = (): string => {
    if (mode === 'role') return role ? t(ROLE_LABEL_KEYS[role]) : '';
    if (mode === 'add') return t(CONTRACT_COMPLIANCE__PLANNING_PANEL_NEW_OVERRIDE);
    return entry?.apiUserName ?? '';
  };

  const renderApis = () => {
    if (mode === 'add' && !selectedApiUserId) return null;
    if (scopeItems.length === 0) {
      return (
        <HintText data-testid={`${PANEL_TEST_ID}-no-apis`}>{t(CONTRACT_COMPLIANCE__PLANNING_NO_LINKED_APIS)}</HintText>
      );
    }
    return (
      <>
        <UPMultipleSelectAutocomplete
          // Remounted per user / override: the field only reads its default value when it mounts.
          key={mode === 'add' ? selectedApiUserId : entry?.id}
          label={t(CONTRACT_COMPLIANCE__PLANNING_APIS_FIELD)}
          placeholder={t(GENERAL__SELECT)}
          items={scopeItems}
          defaultValue={selectedScopeItems}
          handleChange={(_: unknown, value: UPDropdownItem[]) =>
            panel.selectScopes(value.map(item => String(item.value)))
          }
          disabled={isSaving}
          required
          testId={`${PANEL_TEST_ID}-apis`}
        />
        <HintText>{t(CONTRACT_COMPLIANCE__PLANNING_APIS_HINT)}</HintText>
      </>
    );
  };

  const renderBody = () => {
    if (mode === 'add' && userItems.length === 0) {
      return (
        <HintText data-testid={`${PANEL_TEST_ID}-no-eligible-users`}>
          {t(CONTRACT_COMPLIANCE__PLANNING_NO_ELIGIBLE_API_USERS)}
        </HintText>
      );
    }
    return (
      <>
        {mode === 'add' && (
          <>
            <UPDropdownWithSearch
              label={t(CONTRACT_COMPLIANCE__PLANNING_API_USER_FIELD)}
              items={userItems}
              value={selectedApiUserId}
              onChange={value => panel.selectApiUser(value ? String(value) : '')}
              disabled={isSaving}
              required
              testId={`${PANEL_TEST_ID}-api-user`}
            />
            <HintText>{t(CONTRACT_COMPLIANCE__PLANNING_API_USER_HINT)}</HintText>
          </>
        )}
        {isOverride && renderApis()}
        {WORKER_CLASSES.map(workerClass => (
          <UPButtonGroup
            key={workerClass}
            label={workerClass}
            value={states[workerClass]}
            // MUI's exclusive group reports null when the pressed option is clicked again; keep the current value.
            onChange={value => value && panel.setWorkerClassState(workerClass, value as PlanningInThePastState)}
            disabled={isSaving}
          >
            {STATES.map(state => (
              <UPToggleButton
                key={state}
                value={state}
                data-testid={`${PANEL_TEST_ID}-state-${workerClass}-${STATE_SLUGS[state]}`}
              >
                {t(STATE_LABEL_KEYS[state])}
              </UPToggleButton>
            ))}
          </UPButtonGroup>
        ))}
        <HintText>{t(CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT)}</HintText>
        <JustificationFields justification={panel.justification} disabled={isSaving} testIdPrefix={PANEL_TEST_ID} />
        {mode === 'edit' && (
          <RemoveExceptionLink
            type="button"
            onClick={panel.requestRemove}
            disabled={status !== 'ready' || isBusy}
            data-testid={`${PANEL_TEST_ID}-remove`}
          >
            {t(CONTRACT_COMPLIANCE__PLANNING_REMOVE_OVERRIDE)}
          </RemoveExceptionLink>
        )}
      </>
    );
  };

  return (
    <UPSidePanel
      open={status !== 'closed'}
      title={getTitle()}
      // Two-line header like the other side panels, so the card starts at the same height.
      subtitle={t(
        mode === 'role'
          ? CONTRACT_COMPLIANCE__PLANNING_PANEL_ROLE_DEFAULT
          : CONTRACT_COMPLIANCE__PLANNING_PANEL_USER_OVERRIDE,
      )}
      onClose={panel.close}
      testId={PANEL_TEST_ID}
      footer={
        <>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            onClick={panel.requestSave}
            disabled={status !== 'ready' || isBusy || !panel.isComplete}
            testId={`${PANEL_TEST_ID}-save`}
          />
          <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId={`${PANEL_TEST_ID}-close`} />
        </>
      }
    >
      {status === 'loading' && <CircularProgress data-testid={`${PANEL_TEST_ID}-loading`} />}

      {status === 'loadError' && panel.loadErrorMessage && (
        <PanelErrorRow data-testid={`${PANEL_TEST_ID}-load-error`}>
          <ErrorBar message={panel.loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={panel.retryLoad} outlined testId={`${PANEL_TEST_ID}-retry`} />
        </PanelErrorRow>
      )}

      {panel.saveErrorMessage && (
        <PanelErrorRow data-testid={`${PANEL_TEST_ID}-save-error`}>
          <ErrorBar message={panel.saveErrorMessage} />
        </PanelErrorRow>
      )}

      {status === 'ready' && (
        <UPSidePanelSection
          title={t(CONTRACT_COMPLIANCE__PLANNING_PERMISSION_FIELD)}
          testId={`${PANEL_TEST_ID}-details`}
        >
          <>{renderBody()}</>
        </UPSidePanelSection>
      )}
    </UPSidePanel>
  );
};

export default PlanningInThePastPanel;
